import { useState } from "react";
import { Link } from "react-router-dom";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Clock } from "lucide-react";

export interface ResourceArticle {
  id: string;
  title: string;
  category: string;
  readTime: string;
  excerpt: string;
  body: string[];
}

interface ArticleListPageProps {
  title: string;
  intro: string;
  articles: ResourceArticle[];
  cta?: { label: string; to: string };
}

export function ArticleListPage({ title, intro, articles, cta }: ArticleListPageProps) {
  const [openId, setOpenId] = useState<string | null>(null);
  const open = articles.find((a) => a.id === openId);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-16 md:py-24">
        {open ? (
          <article className="max-w-3xl mx-auto">
            <button
              onClick={() => setOpenId(null)}
              className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8"
            >
              <ArrowLeft className="h-4 w-4" /> Retour à la liste
            </button>
            <Badge variant="secondary" className="mb-4">{open.category}</Badge>
            <h1 className="text-3xl md:text-4xl font-bold mb-4">{open.title}</h1>
            <p className="flex items-center gap-2 text-sm text-muted-foreground mb-10">
              <Clock className="h-4 w-4" /> {open.readTime} de lecture
            </p>
            <div className="space-y-5 text-lg leading-relaxed text-foreground/90">
              {open.body.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </article>
        ) : (
          <>
            <div className="max-w-3xl mx-auto text-center mb-16">
              <h1 className="text-4xl md:text-5xl font-bold mb-6">{title}</h1>
              <p className="text-lg text-muted-foreground">{intro}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.map((a) => (
                <Card
                  key={a.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setOpenId(a.id)}
                  onKeyDown={(e) => e.key === "Enter" && setOpenId(a.id)}
                  className="cursor-pointer transition-all hover:-translate-y-1 hover:shadow-lg"
                >
                  <CardHeader>
                    <Badge variant="secondary" className="w-fit mb-2">{a.category}</Badge>
                    <CardTitle className="text-xl leading-snug">{a.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground mb-4">{a.excerpt}</p>
                    <p className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="h-4 w-4" /> {a.readTime}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
            {cta && (
              <div className="text-center mt-16">
                <Link to={cta.to} className="text-primary font-medium hover:underline">
                  {cta.label}
                </Link>
              </div>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
